/* eslint react-hooks/exhaustive-deps: "warn" */

import { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './MaActivityFeaturesSection.module.scss';

type Translate = (key: string) => string;

type TabKey = 'funding' | 'investors' | 'financials' | 'people' | 'ai' | 'competition';

interface TabDef {
  key: TabKey;
  label: string;
  sub: string;
  icon: ReactNode;
}

const IconFunding = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 3v18h18" />
    <path d="M7 14l4-4 3 3 5-6" />
  </svg>
);

const IconInvestors = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="8" r="3" />
    <circle cx="16" cy="10" r="2.4" />
    <path d="M4 20c0-3 2.2-5 5-5s5 2 5 5" />
    <path d="M14.5 20c0-2 1.4-3.5 3.2-3.5" />
  </svg>
);
const IconFinancials = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
);
const IconPeople = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
    <path d="M17 9.5l1.4 1.4L21 8" />
  </svg>
);
const IconAi = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3l2.2 4.6 5 .7-3.6 3.5.9 5-4.5-2.4L7.5 16.8l.9-5L4.8 8.3l5-.7z" />
  </svg>
);
const IconCompetition = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="5" height="9" rx="1" />
    <rect x="10" y="6" width="5" height="14" rx="1" />
    <rect x="17" y="14" width="4" height="6" rx="1" />
  </svg>
);

const getTabs = (t: Translate): TabDef[] => [
  { key: 'funding', label: t('Funding Rounds'), sub: t('Amounts, valuations & exits'), icon: <IconFunding /> },
  { key: 'investors', label: t('Investors'), sub: t('The full capital stack'), icon: <IconInvestors /> },
  {
    key: 'financials',
    label: t('Financials'),
    sub: t('All 600M+ companies, not just funded'),
    icon: <IconFinancials />,
  },
  { key: 'people', label: t('Key People'), sub: t('Who actually signs'), icon: <IconPeople /> },
  { key: 'ai', label: t('AI Insights'), sub: t('Signals that matter'), icon: <IconAi /> },
  { key: 'competition', label: t('Competition'), sub: t('Benchmarked side by side'), icon: <IconCompetition /> },
];

const N = 6;
const AUTO_INTERVAL = 3600;

interface SlideText {
  title: string;
  description: string;
  bullets: string[];
  cta: string;
}

const getSlideText = (t: Translate): Record<TabKey, SlideText> => ({
  funding: {
    title: t('Complete funding rounds, investments & exits'),
    description: t('Every round from seed to late-stage — amounts, valuations, lead investors.'),
    bullets: [
      t('Round type, date, and amount raised'),
      t('Lead investor per round'),
      t('Exits and divestitures with buyer'),
    ],
    cta: t('Explore funding data'),
  },
  investors: {
    title: t('Every investor, fund & LP — classified'),
    description: t('The full capital stack — VC, PE, corporate, sovereign, angels.'),
    bullets: [
      t('Type classification per investor'),
      t('Positions confirmed by registry filings'),
      t('Limited partner mapping'),
    ],
    cta: t('See investor mapping'),
  },
  financials: {
    title: t('Official financial filings — across all 600M+ companies'),
    description: t('Funding databases only see companies that raised. Filings cover every registered company.'),
    bullets: [
      t('P&L, balance sheet & cash flow statements'),
      t('Up to 20 years of filed history'),
      t('Original filing attached to every record'),
    ],
    cta: t('Search 600M+ companies'),
  },
  people: {
    title: t('Leadership, partners & decision-makers'),
    description: t('Roles, bios, and career background — so you know who actually signs.'),
    bullets: [
      t('CEO to board level'),
      t('Directorships verified on registry records'),
      t('Verified direct emails & phone numbers'),
    ],
    cta: t('View key people'),
  },
  ai: {
    title: t('Intelligence that flags what matters'),
    description: t('AI cards surface what matters — no more 50-page filings.'),
    bullets: [
      t('Fundraising momentum and growth signals'),
      t('Deal highlights with approval status'),
      t('Risk factors — regulatory, licensing'),
    ],
    cta: t('See AI insights'),
  },
  competition: {
    title: t('Competitors, identified & benchmarked'),
    description: t(
      'See who a company actually competes with — and how they stack up on filed numbers, not press narratives.',
    ),
    bullets: [
      t('Competitor identification per company'),
      t('Benchmarked on filed financials'),
      t('Customers, revenue, and market coverage side by side'),
    ],
    cta: t('Benchmark competitors'),
  },
});

const PanelFunding = ({ animate }: { animate: boolean }) => (
  <div className={styles.panel}>
    <div className={styles.panelTitle}>Funding Rounds</div>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Date</th>
          <th>Round</th>
          <th>Amount</th>
          <th className={styles.hideM}>Valuation</th>
          <th>Lead investor</th>
        </tr>
      </thead>
      <tbody>
        {[
          { year: '2025', round: 'Growth Equity', amt: '$2B', val: '75.0B', lead: 'Coatue', w: 1 },
          { year: '2024', round: 'Growth Equity', amt: '$500M', val: '45.0B', lead: 'Coatue', w: 0.6 },
          { year: '2021', round: 'Series E+', amt: '$800M', val: '33.0B', lead: 'SoftBank VF2', w: 0.44 },
          { year: '2020', round: 'Series D', amt: '$580M', val: '5.5B', lead: 'TCV', w: 0.09 },
          { year: '2018', round: 'Series C', amt: '$250M', val: '1.7B', lead: 'DST Global', w: 0.05 },
        ].map((r, i) => (
          <tr
            key={r.year + r.round}
            className={cn(styles.row, animate && styles.rowAnimate)}
            style={{ '--delay': `${i * 0.07}s` } as React.CSSProperties}
          >
            <td className={styles.mono}>{r.year}</td>
            <td>
              <span className={cn(styles.tag, styles.tagBlueOutline)}>{r.round}</span>
            </td>
            <td className={styles.strong}>{r.amt}</td>
            <td className={cn(styles.hideM, styles.valCell)}>
              <span className={styles.mono}>{r.val}</span>
              <i
                className={cn(styles.vbar, animate && styles.vbarAnimate)}
                style={{ '--w': r.w, '--delay': `${i * 0.07 + 0.1}s` } as React.CSSProperties}
              />
            </td>
            <td>{r.lead}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className={styles.panelFoot}>
      <span>
        <span className={styles.liveDot} />
        Revolut LTD · 9 rounds tracked
      </span>
      <span>Seed → $75B</span>
    </div>
  </div>
);

const PanelInvestors = ({ animate }: { animate: boolean }) => (
  <div className={styles.panel}>
    <div className={styles.panelTitle}>Investors & Shareholders</div>
    <div className={styles.invGrid}>
      {[
        {
          initials: 'C',
          name: 'Coatue',
          type: 'VC',
          country: 'UNITED STATES',
          desc: 'Co-led the $75B valuation round in Nov 2025 and the $45B secondary sale',
          color: '#3642F5',
          tone: '',
        },
        {
          initials: 'SB',
          name: 'SoftBank Vision Fund 2',
          type: 'CORPORATE',
          country: 'JAPAN',
          desc: 'Co-led Series E at $33B valuation',
          color: '#0E9F6E',
          tone: 'g',
        },
        {
          initials: 'M',
          name: 'Mubadala',
          type: 'GOVERNMENT',
          country: 'UNITED ARAB EMIRATES',
          desc: 'Abu Dhabi sovereign wealth fund; joined 2024 secondary sale',
          color: '#8B5CF6',
          tone: 'p',
        },
        {
          initials: 'B',
          name: 'Balderton Capital',
          type: 'VC',
          country: 'UNITED KINGDOM',
          desc: 'Third largest shareholder per Companies House records; led Series A',
          color: '#0E1526',
          tone: '',
        },
      ].map((inv, i) => (
        <div
          key={inv.name}
          className={cn(
            styles.inv,
            inv.tone && styles[`inv_${inv.tone}` as keyof typeof styles],
            animate && styles.invAnimate,
          )}
          style={{ '--delay': `${i * 0.09}s` } as React.CSSProperties}
        >
          <div className={styles.invTop}>
            <span className={styles.invAv} style={{ background: inv.color }}>
              {inv.initials}
            </span>
            <b>{inv.name}</b>
            <span className={cn(styles.tag, styles.tagBlue)}>{inv.type}</span>
          </div>
          <p>{inv.desc}</p>
          <span className={styles.invCountry}>{inv.country}</span>
        </div>
      ))}
    </div>
    <div className={styles.panelFoot}>
      <span>
        <span className={styles.liveDot} />
        <b>22</b> investors classified
      </span>
      <span>Verified vs. registry filings</span>
    </div>
  </div>
);

const PanelFinancials = ({ animate }: { animate: boolean }) => (
  <div className={styles.panel}>
    <div className={styles.srchHead}>
      <span className={styles.srchTitle}>Company Search</span>
      <span className={styles.srchCount}>12,438 matches</span>
    </div>
    <div className={styles.srch}>
      <div className={styles.srchBar}>
        <svg
          className={styles.srchIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="16.5" y1="16.5" x2="21" y2="21" />
        </svg>
        <span className={styles.srchQ}>Logistics · Germany · filed revenue €10M–50M</span>
      </div>
    </div>
    <div className={cn(styles.fsCo, animate && styles.rowAnimate)} style={{ '--delay': '0s' } as React.CSSProperties}>
      <div>
        <b>Rheinland Logistik GmbH</b>
        <span>Köln, Germany · HRB 44821</span>
      </div>
      <span className={styles.mono}>€38.2M filed revenue · FY2024</span>
    </div>
    <div className={styles.fstGrid}>
      {[
        {
          title: 'Income statement',
          sub: '€M · standardised',
          rows: [
            ['Revenue', '31.5', '34.8', '38.2'],
            ['Gross profit', '9.8', '11.2', '12.6'],
            ['EBITDA', '4.1', '4.9', '5.7'],
          ],
          total: ['Net profit', '2.2', '2.7', '3.1'],
          headers: ['', '2022', '2023', '2024'],
        },
        {
          title: 'Balance sheet',
          sub: '€M',
          rows: [
            ['Total assets', '21.4', '23.9', '26.8'],
            ['Equity', '8.9', '10.2', '11.8'],
            ['Liabilities', '12.5', '13.7', '15.0'],
          ],
          total: ['Working capital', '3.4', '3.9', '4.6'],
          headers: ['', '2022', '2023', '2024'],
        },
      ].map((fst, i) => (
        <div
          key={fst.title}
          className={cn(styles.fst, animate && styles.rowAnimate)}
          style={{ '--delay': `${(i + 1) * 0.1}s` } as React.CSSProperties}
        >
          <div className={styles.fstHead}>
            <b>{fst.title}</b>
            <span>{fst.sub}</span>
          </div>
          <table className={styles.fstTable}>
            <thead>
              <tr>
                {fst.headers.map((h, j) => (
                  <th key={j}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fst.rows.map((r) => (
                <tr key={r[0]}>
                  {r.map((c, j) => (
                    <td key={j}>{c}</td>
                  ))}
                </tr>
              ))}
              <tr className={styles.fstTotal}>
                {fst.total.map((c, j) => (
                  <td key={j}>{c}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
    <div
      className={cn(styles.fsHist, animate && styles.rowAnimate)}
      style={{ '--delay': '0.3s' } as React.CSSProperties}
    >
      <span className={styles.mono}>2005</span>
      <span className={styles.fsLine}>
        <i className={cn(animate && styles.fsLineAnimate)} style={{ '--delay': '0.4s' } as React.CSSProperties} />
      </span>
      <span className={styles.mono}>2024</span>
      <span className={styles.fsTagline}>20 years of history</span>
    </div>
    <div className={styles.panelFoot}>
      <span>
        <span className={styles.liveDot} />
        Original filings attached to every record
      </span>
      <span>Standardised across 200+ countries</span>
    </div>
  </div>
);

const PanelPeople = ({ animate }: { animate: boolean }) => (
  <div className={styles.panel}>
    <div className={styles.panelTitle}>Key People</div>
    <div className={styles.ppGrid}>
      {[
        {
          initials: 'NS',
          name: 'Nikolay Storonsky',
          role: 'CEO & Co-Founder',
          desc: 'Co-founded Revolut in 2015; 65M customers at a $75B valuation.',
          email: 'n······@revolut.com',
          phone: '+44 20 ··· ····',
        },
        {
          initials: 'VY',
          name: 'Vladyslav Yatsenko',
          role: 'CTO & Co-Founder',
          desc: 'Technology vision incl. AI; director on Companies House filings.',
          email: 'v······@revolut.com',
          phone: null,
        },
        {
          initials: 'VS',
          name: 'Victor Stinga',
          role: 'Chief Financial Officer',
          desc: 'Key role in the Nov 2025 round at the $75B valuation.',
          email: 'v·····@revolut.com',
          phone: '+44 20 ··· ····',
        },
        {
          initials: 'BC',
          name: 'Béatrice Cossa-Dumurgier',
          role: 'CEO, Revolut France',
          desc: 'Ex-BNP Paribas; leads the €1.1B investment in France.',
          email: 'b·····@revolut.com',
          phone: null,
        },
      ].map((p, i) => (
        <div
          key={p.name}
          className={cn(styles.pp, animate && styles.ppAnimate)}
          style={{ '--delay': `${i * 0.09}s` } as React.CSSProperties}
        >
          <div className={styles.ppAv}>
            {p.initials}
            <span className={styles.ppCheck}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={3.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
          </div>
          <div>
            <b>{p.name}</b>
            <span>{p.role}</span>
            <p>{p.desc}</p>
            <div
              className={cn(styles.ppContact, animate && styles.ppContactAnimate)}
              style={{ '--delay': `${i * 0.09 + 0.25}s` } as React.CSSProperties}
            >
              <span className={styles.pcItem}>✉ {p.email}</span>
              {p.phone && <span className={styles.pcItem}>✆ {p.phone}</span>}
              <span className={styles.pcBadge}>VERIFIED</span>
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className={styles.panelFoot}>
      <span>
        <span className={styles.liveDot} />
        Directors verified on Companies House
      </span>
      <span>GB 08804411</span>
    </div>
  </div>
);

const PanelAi = ({ animate }: { animate: boolean }) => (
  <div className={styles.panel}>
    <div className={styles.panelTitle}>AI Insights</div>
    <div className={styles.aiGrid}>
      {[
        {
          title: '$75B Valuation in Nov 2025 Secondary Share Sale',
          tag: 'Fundraising momentum',
          tagCls: 'tagBlue',
          body: 'Secondary sale at $75B — a 67% increase from the $45B valuation in Aug 2024.',
          cardCls: '',
        },
        {
          title: 'Revenue Surges 72% to $4B in 2024',
          tag: 'Growth signal',
          tagCls: 'tagGreen',
          body: 'Profit before tax up 149% to $1.4B; one million new customers every 17 days.',
          cardCls: 'aiGreen',
        },
        {
          title: 'UK Banking Licence Still in Mobilization Stage',
          tag: 'Risk factor',
          tagCls: 'tagAmber',
          body: 'Granted Sep 2024 but restricted — adds uncertainty to the IPO timeline.',
          cardCls: 'aiAmber',
        },
      ].map((c, i) => (
        <div
          key={c.title}
          className={cn(
            styles.aiCard,
            c.cardCls && styles[c.cardCls as keyof typeof styles],
            animate && styles.rowAnimate,
          )}
          style={{ '--delay': `${i * 0.1}s` } as React.CSSProperties}
        >
          <div className={styles.aiTop}>
            <b>{c.title}</b>
            <span className={cn(styles.tag, styles[c.tagCls as keyof typeof styles])}>{c.tag}</span>
          </div>
          <p>{c.body}</p>
        </div>
      ))}
    </div>
    <div className={styles.panelFoot}>
      <span>
        <span className={styles.liveDot} />
        <b>5</b> signals this quarter
      </span>
      <span>Generated from filings + verified sources</span>
    </div>
  </div>
);

const PanelCompetition = ({ animate }: { animate: boolean }) => (
  <div className={styles.panel}>
    <div className={styles.panelTitle}>Competition</div>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Company</th>
          <th className={styles.hideM}>HQ</th>
          <th>Customers</th>
          <th>Segment</th>
        </tr>
      </thead>
      <tbody>
        {[
          { name: 'Revolut LTD', hq: 'United Kingdom', cust: '65M', w: 1, subject: true },
          { name: 'Wise', hq: 'United Kingdom', cust: '~16M', w: 0.25, subject: false },
          { name: 'Monzo', hq: 'United Kingdom', cust: '~13M', w: 0.2, subject: false },
          { name: 'N26', hq: 'Germany', cust: '~8M', w: 0.12, subject: false },
          { name: 'Starling Bank', hq: 'United Kingdom', cust: '~4.6M', w: 0.07, subject: false },
        ].map((r, i) => (
          <tr
            key={r.name}
            className={cn(styles.row, r.subject && styles.rowSubject, animate && styles.rowAnimate)}
            style={{ '--delay': `${i * 0.07}s` } as React.CSSProperties}
          >
            <td className={styles.strong}>
              {r.name}
              {r.subject && <span className={cn(styles.tag, styles.tagBlue, styles.subjectTag)}>Subject</span>}
            </td>
            <td className={cn(styles.mono, styles.hideM)}>{r.hq}</td>
            <td className={styles.cmpCell}>
              <span className={styles.mono}>{r.cust}</span>
              <i
                className={cn(styles.cbar, !r.subject && styles.cbarGrey, animate && styles.cbarAnimate)}
                style={{ '--w': r.w, '--delay': `${i * 0.07 + 0.1}s` } as React.CSSProperties}
              />
            </td>
            <td>
              <span className={cn(styles.tag, styles.tagBlueOutline)}>Digital banking</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className={styles.panelFoot}>
      <span>
        <span className={styles.liveDot} />
        Revolut LTD · 12 peers identified
      </span>
      <span>Benchmarked on filed financials</span>
    </div>
  </div>
);

const PANELS: Record<TabKey, React.FC<{ animate: boolean }>> = {
  funding: PanelFunding,
  investors: PanelInvestors,
  financials: PanelFinancials,
  people: PanelPeople,
  ai: PanelAi,
  competition: PanelCompetition,
};

export const MaActivityFeaturesSection = () => {
  const { t } = useInt();
  const { secondaryNavigation } = useContext(ContentContext);
  const bookDemoSlug = secondaryNavigation?.book_demo?.slug;

  const tabs = getTabs(t);
  const slideTextMap = getSlideText(t);

  const trackRef = useRef<HTMLElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLElement | null)[]>([]);

  const inView = useIntersection(trackRef, { continuous: true, threshold: 0.1 });

  const [scrollyActive, setScrollyActive] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [slideAnimKeys, setSlideAnimKeys] = useState<number[]>(() => Array.from({ length: N }, () => 0));
  const [interacted, setInteracted] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const track = trackRef.current;
    const sticky = stickyRef.current;
    if (!track || !sticky) return;

    let engaged = false;
    let sp = 0; // smoothed progress 0..N-1
    let currentActive = -1;
    let raf: number | undefined;
    let topOff = 0;

    const clamp = (v: number, a: number, b: number) => Math.min(Math.max(v, a), b);
    const smoothstep = (t: number) => t * t * (3 - 2 * t);
    const travel = () => Math.max(track.offsetHeight - sticky.offsetHeight, 1);

    const targetProgress = () => {
      const total = travel();
      if (total <= 0) return 0;
      const passed = clamp(-track.getBoundingClientRect().top + topOff, 0, total);
      const raw = (passed / total) * (N - 1);
      const seg = Math.floor(raw);
      const frac = raw - seg;
      return clamp(seg + smoothstep(clamp((frac - 0.3) / 0.4, 0, 1)), 0, N - 1);
    };

    const paint = () => {
      slideRefs.current.forEach((s, k) => {
        if (!s) return;
        const d = sp - k;
        const ad = Math.abs(d);
        const o = clamp(1 - ad * 2.4, 0, 1);
        s.style.opacity = o.toFixed(3);
        s.style.transform = `translateY(${(d * -34).toFixed(1)}px)`;
      });
      const idx = clamp(Math.round(sp), 0, N - 1);
      if (idx !== currentActive) {
        currentActive = idx;
        setActiveIdx(idx);
        setSlideAnimKeys((prev) => {
          const next = [...prev];
          next[idx] = (next[idx] || 0) + 1;
          return next;
        });
      }
    };

    const loop = () => {
      if (!engaged) {
        return;
      }

      const tp = targetProgress();
      sp += (tp - sp) * 0.14;
      if (Math.abs(tp - sp) < 0.0015) sp = tp;
      paint();
      raf = requestAnimationFrame(loop);
    };

    const engage = () => {
      try {
        const isMobile = window.matchMedia('(max-width: 960px)').matches;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (isMobile || reduced) {
          disengage();
          return;
        }

        topOff = Math.min(Math.max((window.innerHeight - sticky.offsetHeight) / 2, 0), 64);
        sticky.style.top = `${topOff}px`;
        track.style.height = `${sticky.offsetHeight + window.innerHeight * 0.72 * N}px`;
        setScrollyActive(true);

        if (!engaged) {
          engaged = true;
          sp = targetProgress();
          paint();
          raf = requestAnimationFrame(loop);
        }
      } catch {
        disengage();
      }
    };

    const disengage = () => {
      engaged = false;
      if (raf) {
        cancelAnimationFrame(raf);
      }

      track.style.height = 'auto';
      sticky.style.top = '';
      slideRefs.current.forEach((s) => {
        if (!s) {
          return;
        }

        s.style.opacity = '';
        s.style.transform = '';
      });
      setScrollyActive(false);
      currentActive = -1;
    };

    window.addEventListener('resize', engage);
    engage();

    return () => {
      window.removeEventListener('resize', engage);
      engaged = false;
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const onTabClick = useCallback((i: number) => {
    setInteracted(true);

    const track = trackRef.current;
    const sticky = stickyRef.current;
    if (track && sticky && sticky.style.top !== '') {
      const topOff = parseFloat(sticky.style.top) || 0;
      const travelDist = Math.max(track.offsetHeight - sticky.offsetHeight, 1);
      const y = window.scrollY + track.getBoundingClientRect().top - topOff + (i / (N - 1)) * travelDist;
      window.scrollTo({ top: y, behavior: 'smooth' });

      return;
    }

    setActiveIdx(i);
    setSlideAnimKeys((prev) => {
      const next = [...prev];
      next[i] = (next[i] || 0) + 1;
      return next;
    });
  }, []);

  useEffect(() => {
    if (scrollyActive || interacted || !inView) {
      return;
    }

    timerRef.current = setTimeout(() => {
      setActiveIdx((cur) => {
        const next = (cur + 1) % N;
        setSlideAnimKeys((prev) => {
          const arr = [...prev];
          arr[next] = (arr[next] || 0) + 1;
          return arr;
        });
        return next;
      });
    }, AUTO_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [scrollyActive, interacted, inView, activeIdx]);

  return (
    <section className={styles.track} ref={trackRef}>
      <div className={cn(styles.sticky, scrollyActive && styles.stickyScrolly)} ref={stickyRef}>
        <div className={styles.intro}>
          <TitleBlock
            className={styles.titleBlock}
            title={t("Everything you need. Nothing you don't.")}
            description={t(
              'Start with a company. End with the full deal picture — acquisitions, funding, investors, people, and the signals behind the next move.',
            )}
          />
        </div>

        <div className={styles.grid}>
          <aside className={styles.tabNav}>
            {tabs.map((tab, i) => (
              <button
                key={tab.key}
                className={cn(styles.tabBtn, activeIdx === i && styles.tabBtnActive)}
                onClick={() => onTabClick(i)}
              >
                <span className={styles.tabIcon}>{tab.icon}</span>
                <span className={styles.tabLabel}>{tab.label}</span>
                <span className={styles.tabSub}>{tab.sub}</span>
              </button>
            ))}
          </aside>

          <div className={cn(styles.scStage, scrollyActive && styles.scStageScrolly)}>
            {tabs.map((tab, i) => {
              const slideText = slideTextMap[tab.key];
              const SlidePanel = PANELS[tab.key];
              const isActive = activeIdx === i;
              return (
                <article
                  key={tab.key}
                  ref={(el) => {
                    slideRefs.current[i] = el;
                  }}
                  className={cn(styles.scSlide, isActive && styles.scSlideOn)}
                >
                  <div className={styles.slideText}>
                    <h3 className={styles.slideTitle}>{slideText.title}</h3>
                    <p className={styles.slideDesc}>{slideText.description}</p>
                    <ul className={styles.featureList}>
                      {slideText.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                    <a href={bookDemoSlug || '/book-a-demo'} className={styles.slideCta}>
                      {slideText.cta}
                    </a>
                  </div>
                  <div className={styles.slideVisual}>
                    <SlidePanel key={slideAnimKeys[i]} animate={isActive} />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
